import { useState } from "react";
import { Link } from "react-router-dom";
import { Upload, FileSpreadsheet, CheckCircle2, AlertCircle, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { tierLabel, isBusinessTier } from "@/lib/tiers";
import { useAuth } from "@/context/AuthContext";

const SAMPLE_HEADERS = [
  "asset_type", "title", "address", "city", "state", "zip_code", "asking_price",
  "listing_url", "description", "days_on_market", "price_reductions",
  "industry", "revenue", "cash_flow", "ebitda", "employees",
  "units", "noi", "occupancy", "cap_rate", "year_built",
  "acreage", "zoning", "utilities", "development_potential", "county",
  "owner_name", "owner_phone", "owner_email", "absentee_owner", "tax_delinquent",
  "seller_financing_flag",
];

const SAMPLE_CSV = `asset_type,title,city,state,asking_price,description
business,Riverside Bakery,Austin,TX,425000,Profitable bakery owner wants to retire next year
multifamily,12-Unit Garden Apts,Tampa,FL,1450000,Below-market rents value-add
land,40 Acres Hwy Frontage,Boise,ID,890000,Long-held parcel owner open to seller financing`;

export default function BulkImport() {
  const { user } = useAuth();
  const tier = (user?.tier || "free").toLowerCase();
  const hasBusinessTier = isBusinessTier(tier) || user?.role === "admin" || user?.role === "super_admin";
  const businessLabel = tierLabel("business"); // resolves to "BUSINESS"
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [shareBatch, setShareBatch] = useState(false);

  if (!hasBusinessTier) {
    return (
      <div className="space-y-6" data-testid="bulk-import-upgrade-gate">
        <header>
          <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2">{businessLabel}</div>
          <h1 className="font-heading text-4xl tracking-tight">Bulk CSV Import</h1>
        </header>
        <div className="ae-card p-8 max-w-2xl">
          <div className="flex items-center gap-3 mb-4">
            <Lock className="h-6 w-6 text-emerald-400" strokeWidth={1.5} />
            <div className="text-[10px] uppercase tracking-widest text-emerald-400">Upgrade Required</div>
          </div>
          <h2 className="font-heading text-2xl tracking-tight">Bulk CSV import is a {businessLabel} feature.</h2>
          <p className="text-sm text-slate-400 mt-3 leading-relaxed">
            Upgrade to import your entire deal list at once. {businessLabel} also unlocks webhook ingestion so your data partners can push deals directly into your pipeline.
          </p>
          <div className="mt-6 flex items-center gap-3">
            <Link to="/app/billing" data-testid="bulk-import-upgrade-cta" className="btn-primary text-sm">
              View {businessLabel} plan
            </Link>
            <Link to="/app/intake" className="btn-ghost text-sm">
              Add one opportunity instead
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("share", shareBatch ? "true" : "false");
      const r = await api.post("/ingest/csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
      toast.success(`Imported ${r.data.inserted} opportunities${shareBatch ? " to the shared pool" : " as private"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const downloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sample-opportunities.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <header>
        <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2">{businessLabel}</div>
        <h1 className="font-heading text-4xl tracking-tight">Bulk Import</h1>
        <p className="text-sm text-slate-400 mt-1">Upload CSV of opportunities. Useful for licensed data sources and broker exports.</p>
      </header>

      <div className="ae-card p-6">
        <div className="flex items-center gap-2 mb-3">
          <FileSpreadsheet className="h-4 w-4 text-emerald-400" />
          <div className="text-[10px] uppercase tracking-widest text-slate-500">CSV Upload</div>
        </div>

        <div
          className="border-2 border-dashed border-white/10 rounded-sm p-8 text-center hover:border-emerald-500/40 transition-colors cursor-pointer"
          onClick={() => document.getElementById("bulk-file").click()}
        >
          <Upload className="h-8 w-8 text-slate-500 mx-auto mb-2" />
          <div className="text-sm text-slate-300">{file ? file.name : "Click to choose CSV file"}</div>
          <div className="text-xs text-slate-500 mt-1">{file ? `${(file.size / 1024).toFixed(1)} KB` : "or drag and drop"}</div>
          <input
            id="bulk-file"
            data-testid="bulk-file-input"
            type="file"
            accept=".csv"
            className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
        </div>

        <div className="flex gap-2 mt-4">
          <button onClick={upload} disabled={!file || busy} data-testid="bulk-upload-btn" className="btn-primary text-sm inline-flex items-center gap-2">
            <Upload className="h-4 w-4" /> {busy ? "Uploading…" : "Upload & Import"}
          </button>
          <button onClick={downloadSample} data-testid="bulk-sample-btn" className="btn-ghost text-sm">Download sample CSV</button>
        </div>

        <label className="mt-4 flex items-start gap-3 p-3 rounded-sm border border-white/10 bg-white/[0.02] cursor-pointer" data-testid="bulk-share-toggle-wrap">
          <input type="checkbox" className="mt-0.5" checked={shareBatch} onChange={(e) => setShareBatch(e.target.checked)}
                 data-testid="bulk-share-toggle" />
          <span className="text-xs text-slate-300 leading-relaxed">
            <strong className="text-slate-100">Publish this batch to the shared discovery pool.</strong>
            <span className="block text-slate-500 mt-0.5">
              Off by default: imported rows stay private to your account. Turn this on if you want every subscriber to be able to browse the rows you just uploaded.
            </span>
          </span>
        </label>


        {result && (
          <div className="mt-5 ae-card p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-400" />
              <div className="font-medium" data-testid="bulk-result">
                Imported {result.inserted} · Skipped {result.skipped}
              </div>
            </div>
            {result.errors?.length > 0 && (
              <div className="mt-2">
                <div className="flex items-center gap-1.5 text-rose-400 text-xs">
                  <AlertCircle className="h-3.5 w-3.5" /> {result.errors.length} errors
                </div>
                <ul className="text-xs text-slate-500 mt-1 space-y-0.5 font-mono">
                  {result.errors.slice(0, 8).map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="ae-card p-6">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Expected Columns</div>
        <p className="text-xs text-slate-400 mb-3">
          Required: <span className="text-slate-200 font-mono">asset_type</span> (business/multifamily/land),
          <span className="text-slate-200 font-mono"> title</span>, <span className="text-slate-200 font-mono">city</span>, <span className="text-slate-200 font-mono">state</span>. All others optional.
        </p>
        <div className="flex flex-wrap gap-1">
          {SAMPLE_HEADERS.map((h) => (
            <code key={h} className="text-[10px] font-mono bg-white/5 border border-white/10 rounded-sm px-1.5 py-0.5">{h}</code>
          ))}
        </div>
      </div>

      <div className="ae-card p-6">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Webhook Ingestion (API)</div>
        <p className="text-xs text-slate-400 mb-3">Third-party data providers can push opportunities directly via webhook:</p>
        <pre className="bg-black/40 border border-white/10 rounded-sm p-3 text-[11px] font-mono overflow-x-auto">
{`POST /api/ingest/webhook
Content-Type: application/json

{
  "api_key": "your-shared-secret",
  "source": "data-partner-name",
  "items": [
    { "asset_type": "business", "title": "...", "city": "...", "state": "..." }
  ]
}`}
        </pre>
      </div>
    </div>
  );
}
